import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, Search, Pencil, Trash2, Mail, MailPlus, Download, Filter,
  AlertTriangle, CheckCircle2, XCircle, Clock, Paperclip, X, ArrowUp, ArrowDown,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

function formatMoney(value, currency = "EUR") {
  if (value == null || isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("es-ES", {
    style: "currency", currency, minimumFractionDigits: 0, maximumFractionDigits: 2, useGrouping: "always",
  });
}

/**
 * ClientAutocomplete
 * Debounced type-ahead for the debt client field. When the user picks a
 * suggestion, populates CIF, email, and contact_person in one click — saving
 * the repetitive typing when the same client keeps falling behind on payments.
 */
function ClientAutocomplete({ value, onChange, onPick, lang }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // Fetch suggestions with 200ms debounce. Empty query still returns the top
  // recent clients so the dropdown is useful even with a fresh (empty) input.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (value) params.set("q", value);
        params.set("limit", "10");
        const { data } = await api.get(`/debts/client-suggestions?${params.toString()}`);
        setSuggestions(data?.suggestions || []);
      } catch (e) {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  // Close dropdown on outside click.
  useEffect(() => {
    const handler = (ev) => {
      if (wrapRef.current && !wrapRef.current.contains(ev.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pick = (s) => {
    onPick?.(s);
    setOpen(false);
    setHighlightIdx(-1);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && highlightIdx >= 0) {
      e.preventDefault();
      pick(suggestions[highlightIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <Input
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        autoFocus
        autoComplete="off"
        data-testid="debt-client"
      />
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-md border border-[var(--border-color)] bg-[var(--surface)] shadow-lg"
          data-testid="debt-client-suggestions"
        >
          {loading && (
            <div className="px-3 py-2 text-xs text-[var(--text-muted)]">
              {lang === "es" ? "Buscando..." : "Searching..."}
            </div>
          )}
          {suggestions.map((s, idx) => (
            <button
              key={`${s.tax_id || s.name}-${idx}`}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(s)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-[var(--surface-hover)] ${idx === highlightIdx ? "bg-[var(--surface-hover)]" : ""}`}
              data-testid={`debt-client-suggestion-${idx}`}
            >
              <div className="font-medium truncate">{s.name}</div>
              <div className="text-xs text-[var(--text-muted)] flex flex-wrap gap-x-2">
                {s.tax_id && <span className="font-mono-num">{s.tax_id}</span>}
                {s.email && <span>· {s.email}</span>}
                {(s.sources || []).includes("debt") && (
                  <span className="text-[var(--status-warning)]">· {lang === "es" ? "de deudas previas" : "from past debts"}</span>
                )}
                {(s.sources || []).includes("invoice") && !(s.sources || []).includes("debt") && (
                  <span className="text-[var(--text-muted)]">· {lang === "es" ? "de facturas" : "from invoices"}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


const STATUS_META = {
  pending:   { es: "Pendiente", en: "Pending",   fg: "text-amber-600",             bg: "bg-amber-500/15",           icon: Clock },
  paid:      { es: "Pagado",    en: "Paid",      fg: "text-[var(--status-positive)]", bg: "bg-emerald-500/15",       icon: CheckCircle2 },
  cancelled: { es: "Anulado",   en: "Cancelled", fg: "text-[var(--text-muted)]",   bg: "bg-neutral-500/15",         icon: XCircle },
};

export default function Deudas() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const baseCurrency = user?.base_currency || "EUR";

  const [debts, setDebts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [year, setYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  // Per-column filters (client-side, applied over the loaded rows)
  const [clientFilter, setClientFilter] = useState("");
  const [taxIdFilter, setTaxIdFilter] = useState("");
  const [contactFilter, setContactFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [showAdvFilters, setShowAdvFilters] = useState(false);

  // Sort: {column, order: 'asc'|'desc'}
  const [sort, setSort] = useState({ column: "event_date", order: "desc" });

  const [selectedIds, setSelectedIds] = useState(new Set());

  const [editing, setEditing] = useState(null); // debt object or {} for new
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (year) params.year = year;
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const summaryParams = year ? { year } : {};
      const [d, s] = await Promise.all([
        api.get("/debts", { params }),
        api.get("/debts/summary", { params: summaryParams }),
      ]);
      setDebts(d.data.debts || []);
      setSummary(s.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [year, statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  // Client-side filtering + sorting layered on top of the server response.
  // The server already handles global search/status/year filters; these
  // per-column controls narrow the visible rows without a re-fetch.
  const visibleDebts = useMemo(() => {
    let arr = debts;
    if (clientFilter.trim()) {
      const q = clientFilter.trim().toLowerCase();
      arr = arr.filter((d) => (d.client_name || "").toLowerCase().includes(q));
    }
    if (taxIdFilter.trim()) {
      const q = taxIdFilter.trim().toLowerCase();
      arr = arr.filter((d) => (d.client_tax_id || "").toLowerCase().includes(q));
    }
    if (contactFilter.trim()) {
      const q = contactFilter.trim().toLowerCase();
      arr = arr.filter((d) => (d.contact_person || "").toLowerCase().includes(q));
    }
    if (emailFilter.trim()) {
      const q = emailFilter.trim().toLowerCase();
      arr = arr.filter((d) => (d.email || "").toLowerCase().includes(q));
    }
    if (dateFrom) arr = arr.filter((d) => (d.event_date || "") >= dateFrom);
    if (dateTo) arr = arr.filter((d) => (d.event_date || "") <= dateTo);
    if (minAmount !== "" && !isNaN(parseFloat(minAmount))) {
      const min = parseFloat(minAmount);
      arr = arr.filter((d) => Number(d.amount || 0) >= min);
    }
    if (maxAmount !== "" && !isNaN(parseFloat(maxAmount))) {
      const max = parseFloat(maxAmount);
      arr = arr.filter((d) => Number(d.amount || 0) <= max);
    }
    // Sort
    const col = sort.column;
    const dir = sort.order === "asc" ? 1 : -1;
    const sorted = [...arr].sort((a, b) => {
      const va = a[col];
      const vb = b[col];
      if (col === "amount") return (Number(va || 0) - Number(vb || 0)) * dir;
      const sa = (va || "").toString().toLowerCase();
      const sb = (vb || "").toString().toLowerCase();
      if (sa < sb) return -1 * dir;
      if (sa > sb) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [debts, clientFilter, taxIdFilter, contactFilter, emailFilter, dateFrom, dateTo, minAmount, maxAmount, sort]);

  const toggleSort = (column) => {
    setSort((prev) => {
      if (prev.column !== column) return { column, order: "asc" };
      return { column, order: prev.order === "asc" ? "desc" : "asc" };
    });
  };

  const hasAdvFilters = clientFilter || taxIdFilter || contactFilter || emailFilter || dateFrom || dateTo || minAmount !== "" || maxAmount !== "";
  const clearAdvFilters = () => {
    setClientFilter(""); setTaxIdFilter(""); setContactFilter(""); setEmailFilter("");
    setDateFrom(""); setDateTo("");
    setMinAmount(""); setMaxAmount("");
  };

  const total = visibleDebts.reduce((acc, d) => acc + Number(d.amount || 0), 0);
  const hasSelection = selectedIds.size > 0;
  const sendgridReady = summary?.sendgrid_configured;

  const openNew = () => {
    setEditing({
      client_name: "", client_tax_id: "", concept: "", event_date: new Date().toISOString().slice(0, 10),
      invoice_number: "", contact_person: "", email: "", amount: "",
      currency: baseCurrency, status: "pending", notes: "", due_days: 30,
    });
    setDialogOpen(true);
  };
  const openEdit = (d) => {
    setEditing({ ...d, amount: String(d.amount ?? "") });
    setDialogOpen(true);
  };
  const closeDialog = () => { setDialogOpen(false); setEditing(null); };

  const submitDialog = async () => {
    if (!editing.client_name?.trim()) { toast.error(lang === "es" ? "Cliente requerido" : "Client required"); return; }
    const amt = parseFloat(editing.amount);
    if (isNaN(amt)) { toast.error(lang === "es" ? "Importe inválido" : "Invalid amount"); return; }
    const payload = {
      client_name: editing.client_name.trim(),
      client_tax_id: (editing.client_tax_id || "").trim().toUpperCase() || null,
      concept: editing.concept || "",
      event_date: editing.event_date,
      invoice_number: editing.invoice_number || null,
      contact_person: editing.contact_person || null,
      email: editing.email || null,
      amount: amt,
      currency: (editing.currency || "EUR").toUpperCase(),
      status: editing.status || "pending",
      notes: editing.notes || null,
      due_days: parseInt(editing.due_days, 10) || 30,
    };
    try {
      let debtId;
      if (editing.id) {
        await api.put(`/debts/${editing.id}`, payload);
        debtId = editing.id;
        toast.success(lang === "es" ? "Deuda actualizada" : "Debt updated");
      } else {
        const { data } = await api.post("/debts", payload);
        debtId = data?.id;
        toast.success(lang === "es" ? "Deuda creada" : "Debt created");
      }
      // Upload attachment if the user selected one in the dialog
      const attachedFile = editing.__attachedFile;
      if (debtId && attachedFile) {
        try {
          const fd = new FormData();
          fd.append("file", attachedFile);
          await api.post(`/debts/${debtId}/attach`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success(lang === "es" ? "Factura adjuntada" : "Invoice attached");
        } catch (err) {
          toast.error(formatApiError(err));
        }
      }
      closeDialog();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDelete = async (d) => {
    if (!window.confirm(lang === "es" ? `¿Eliminar deuda de ${d.client_name}?` : `Delete debt from ${d.client_name}?`)) return;
    try {
      await api.delete(`/debts/${d.id}`);
      toast.success(lang === "es" ? "Eliminado" : "Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const markPaid = async (d) => {
    try {
      await api.put(`/debts/${d.id}`, { status: "paid" });
      toast.success(lang === "es" ? "Marcada como pagada" : "Marked as paid");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendOne = async (d) => {
    if (!d.email) { toast.error(lang === "es" ? "Sin email de cliente" : "No client email"); return; }
    try {
      const { data } = await api.post(`/debts/${d.id}/send-reminder`);
      if (data.sent) toast.success(lang === "es" ? "Recordatorio enviado" : "Reminder sent");
      else if (data.mocked) toast.warning(lang === "es"
        ? "SendGrid no configurado — configura SENDGRID_API_KEY para enviar realmente"
        : "SendGrid not configured — set SENDGRID_API_KEY to actually send");
      else toast.error(lang === "es" ? `No enviado: ${data.reason}` : `Not sent: ${data.reason}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendBulk = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    try {
      const { data } = await api.post("/debts/send-reminders", { ids });
      const withAtt = (data.results || []).filter((r) => r.has_attachment).length;
      const attNote = withAtt > 0
        ? (lang === "es" ? ` · ${withAtt} con factura adjunta` : ` · ${withAtt} with invoice attached`)
        : "";
      if (data.sendgrid_configured) {
        toast.success(lang === "es"
          ? `Recordatorios enviados: ${data.sent_count}/${ids.length}${attNote}`
          : `Reminders sent: ${data.sent_count}/${ids.length}${attNote}`);
      } else {
        toast.warning(lang === "es"
          ? `SendGrid no configurado (mock). Se habrían enviado ${data.mocked_count} recordatorios${attNote}.`
          : `SendGrid not configured (mock). Would have sent ${data.mocked_count} reminders${attNote}.`);
      }
      setSelectedIds(new Set());
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportXlsx = async () => {
    try {
      const params = {};
      if (year) params.year = year;
      if (statusFilter) params.status = statusFilter;
      const res = await api.get("/debts/export/xlsx", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `deudas_${year || "todos"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectedIds.size === visibleDebts.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(visibleDebts.map((d) => d.id)));
  };

  // Attach / detach an invoice PDF for a specific debt row. Reuses a hidden
  // file input keyed by the target debt id.
  const attachRef = useRef({});
  const openAttachPicker = (id) => {
    const el = attachRef.current[id];
    if (el) el.click();
  };
  const uploadAttachment = async (id, fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/debts/${id}/attach`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(lang === "es" ? "Factura adjuntada" : "Invoice attached");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      const el = attachRef.current[id];
      if (el) el.value = "";
    }
  };
  const detachAttachment = async (d) => {
    if (!window.confirm(lang === "es" ? `¿Quitar factura adjunta a ${d.client_name}?` : `Detach invoice from ${d.client_name}?`)) return;
    try {
      await api.delete(`/debts/${d.id}/attach`);
      toast.success(lang === "es" ? "Adjunto eliminado" : "Attachment removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const buckets = summary?.buckets || {};

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)] mb-2">
            {lang === "es" ? "Cuentas por cobrar" : "Accounts receivable"}
          </p>
          <h1 className="font-display text-4xl font-bold tracking-tight leading-none">
            {lang === "es" ? "Deudas de clientes" : "Client debts"}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">
            {lang === "es"
              ? "Seguimiento de importes pendientes de cobro. Envía recordatorios por email a tus clientes."
              : "Track outstanding client payments. Send email reminders directly."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select
            value={year ? String(year) : "total"}
            onValueChange={(v) => setYear(v === "total" ? null : (parseInt(v, 10) || new Date().getFullYear()))}
          >
            <SelectTrigger className="h-9 w-40 font-mono-num" data-testid="debts-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(() => {
                const cy = new Date().getFullYear();
                return [cy, cy - 1, cy - 2, cy - 3, cy - 4].map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ));
              })()}
              <SelectItem value="total">{lang === "es" ? "Total histórico" : "Historic total"}</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={exportXlsx} data-testid="debts-export-xlsx">
            <Download size={14} className="mr-1.5" />
            Excel
          </Button>
          <Button
            size="sm"
            onClick={openNew}
            className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
            data-testid="debts-new-btn"
          >
            <Plus size={14} className="mr-1.5" />
            {lang === "es" ? "Nueva deuda" : "New debt"}
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard
          label={lang === "es" ? "Pendiente" : "Pending"}
          count={buckets.pending?.count || 0}
          amount={buckets.pending?.amount || 0}
          currency={baseCurrency}
          color="amber"
          testid="summary-pending"
        />
        <SummaryCard
          label={lang === "es" ? "Vencido" : "Overdue"}
          count={buckets.overdue?.count || 0}
          amount={buckets.overdue?.amount || 0}
          currency={baseCurrency}
          color="red"
          testid="summary-overdue"
        />
        <SummaryCard
          label={lang === "es" ? "Cobrado" : "Paid"}
          count={buckets.paid?.count || 0}
          amount={buckets.paid?.amount || 0}
          currency={baseCurrency}
          color="green"
          testid="summary-paid"
        />
        <SummaryCard
          label={lang === "es" ? "Anulado" : "Cancelled"}
          count={buckets.cancelled?.count || 0}
          amount={buckets.cancelled?.amount || 0}
          currency={baseCurrency}
          color="gray"
          testid="summary-cancelled"
        />
      </div>

      {/* SendGrid banner */}
      {summary && !sendgridReady && (
        <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/50 rounded-lg px-4 py-3 flex items-start gap-3" data-testid="sendgrid-banner">
          <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 dark:text-amber-200">
            <strong>{lang === "es" ? "SendGrid no está configurado." : "SendGrid is not configured."}</strong>
            {" "}
            {lang === "es"
              ? "Los botones de recordatorio están habilitados pero no enviarán emails reales. Añade SENDGRID_API_KEY + SENDGRID_FROM_EMAIL en el backend para activarlos."
              : "Reminder buttons are enabled but won't send real emails. Set SENDGRID_API_KEY + SENDGRID_FROM_EMAIL in the backend to activate."}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-3 flex items-center gap-2 flex-wrap" data-testid="debts-toolbar">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none" />
          <Input
            placeholder={lang === "es" ? "Buscar cliente / concepto / factura..." : "Search client / concept / invoice..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="debts-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-md border border-[var(--border-color)] bg-[var(--surface)] px-2 text-sm"
          data-testid="debts-status-filter"
        >
          <option value="">{lang === "es" ? "Todos los estados" : "All statuses"}</option>
          <option value="pending">{lang === "es" ? "Pendiente" : "Pending"}</option>
          <option value="overdue">{lang === "es" ? "Vencido" : "Overdue"}</option>
          <option value="paid">{lang === "es" ? "Pagado" : "Paid"}</option>
          <option value="cancelled">{lang === "es" ? "Anulado" : "Cancelled"}</option>
        </select>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowAdvFilters((v) => !v)}
          data-testid="debts-toggle-adv-filters"
          className={hasAdvFilters ? "border-[var(--primary)] text-[var(--primary)]" : ""}
        >
          <Filter size={14} className="mr-1.5" />
          {lang === "es" ? "Filtros avanzados" : "Advanced filters"}
          {hasAdvFilters && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-[var(--primary)]" />}
        </Button>
        {hasSelection && (
          <Button
            size="sm"
            variant="outline"
            onClick={sendBulk}
            className="border-[var(--primary)] text-[var(--primary)]"
            data-testid="debts-bulk-send"
          >
            <MailPlus size={14} className="mr-1.5" />
            {lang === "es" ? `Enviar recordatorio (${selectedIds.size})` : `Send reminder (${selectedIds.size})`}
          </Button>
        )}
      </div>

      {/* Advanced per-column filters */}
      {showAdvFilters && (
        <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-4 grid grid-cols-1 md:grid-cols-8 gap-3" data-testid="debts-adv-filters">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{lang === "es" ? "Cliente" : "Client"}</Label>
            <Input value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} placeholder={lang === "es" ? "Cliente..." : "Client..."} className="h-9" data-testid="debts-filter-client" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">CIF</Label>
            <Input value={taxIdFilter} onChange={(e) => setTaxIdFilter(e.target.value)} placeholder="B12345..." className="h-9 font-mono-num uppercase" data-testid="debts-filter-taxid" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{lang === "es" ? "Contacto" : "Contact"}</Label>
            <Input value={contactFilter} onChange={(e) => setContactFilter(e.target.value)} placeholder={lang === "es" ? "Persona..." : "Person..."} className="h-9" data-testid="debts-filter-contact" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Email</Label>
            <Input value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} placeholder="@..." className="h-9" data-testid="debts-filter-email" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{lang === "es" ? "Fecha desde" : "Date from"}</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 font-mono-num" data-testid="debts-filter-date-from" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{lang === "es" ? "Fecha hasta" : "Date to"}</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 font-mono-num" data-testid="debts-filter-date-to" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{lang === "es" ? "Importe mín" : "Min amount"}</Label>
            <Input type="number" step="0.01" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} placeholder="0" className="h-9 font-mono-num" data-testid="debts-filter-min-amount" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{lang === "es" ? "Importe máx" : "Max amount"}</Label>
            <Input type="number" step="0.01" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} placeholder="∞" className="h-9 font-mono-num" data-testid="debts-filter-max-amount" />
          </div>
          {hasAdvFilters && (
            <div className="md:col-span-8 flex justify-end">
              <button
                onClick={clearAdvFilters}
                className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] underline"
                data-testid="debts-filter-clear"
              >
                {lang === "es" ? "Limpiar filtros" : "Clear filters"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]" data-testid="debts-table">
          <thead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] bg-[var(--surface-hover)]/40">
            <tr className="border-b border-[var(--border-color)]">
              <th className="px-3 py-2 w-8">
                <input
                  type="checkbox"
                  checked={visibleDebts.length > 0 && selectedIds.size === visibleDebts.length}
                  onChange={toggleAll}
                  data-testid="debts-select-all"
                  className="rounded border-[var(--border-color)]"
                  aria-label="select all"
                />
              </th>
              <SortHeader label={lang === "es" ? "Cliente" : "Client"} column="client_name" sort={sort} onToggle={toggleSort} testid="sort-client" />
              <SortHeader label="CIF" column="client_tax_id" sort={sort} onToggle={toggleSort} testid="sort-taxid" />
              <SortHeader label={lang === "es" ? "Concepto" : "Concept"} column="concept" sort={sort} onToggle={toggleSort} testid="sort-concept" />
              <SortHeader label={lang === "es" ? "Fecha evento" : "Event date"} column="event_date" sort={sort} onToggle={toggleSort} testid="sort-date" />
              <th className="text-left px-3 py-2 font-medium">{lang === "es" ? "Nº fra" : "Invoice #"}</th>
              <th className="text-left px-3 py-2 font-medium">{lang === "es" ? "Contacto" : "Contact"}</th>
              <th className="text-left px-3 py-2 font-medium">Email</th>
              <SortHeader label={lang === "es" ? "Importe" : "Amount"} column="amount" sort={sort} onToggle={toggleSort} align="right" testid="sort-amount" />
              <SortHeader label={lang === "es" ? "Estado" : "Status"} column="status" sort={sort} onToggle={toggleSort} testid="sort-status" />
              <th className="text-left px-3 py-2 font-medium">{lang === "es" ? "Adjunto" : "File"}</th>
              <th className="text-left px-3 py-2 font-medium">{lang === "es" ? "Observaciones" : "Notes"}</th>
              <th className="text-right px-3 py-2 font-medium w-28">{lang === "es" ? "Acciones" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={13} className="px-3 py-10 text-center text-[var(--text-muted)]">…</td></tr>
            )}
            {!loading && visibleDebts.length === 0 && (
              <tr>
                <td colSpan={13} className="px-3 py-16 text-center text-[var(--text-muted)]">
                  {lang === "es" ? "Sin deudas visibles con los filtros actuales." : "No debts match the current filters."}
                </td>
              </tr>
            )}
            {!loading && visibleDebts.map((d) => {
              const meta = STATUS_META[d.status] || STATUS_META.pending;
              const StatusIcon = meta.icon;
              const isSelected = selectedIds.has(d.id);
              return (
                <tr
                  key={d.id}
                  className={`border-t border-[var(--border-color)] hover:bg-[var(--surface-hover)]/30 ${isSelected ? "bg-[var(--primary)]/5" : ""}`}
                  data-testid={`debt-row-${d.id}`}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(d.id)}
                      data-testid={`debt-select-${d.id}`}
                      className="rounded border-[var(--border-color)]"
                    />
                  </td>
                  <td className="px-3 py-2 font-medium truncate max-w-[220px]" title={d.client_name}>
                    {d.client_name}
                  </td>
                  <td className="px-3 py-2 font-mono-num text-xs text-[var(--text-secondary)]" data-testid={`debt-taxid-${d.id}`}>
                    {d.client_tax_id || "—"}
                  </td>
                  <td className="px-3 py-2 text-[var(--text-secondary)] truncate max-w-[220px]" title={d.concept}>{d.concept || "—"}</td>
                  <td className="px-3 py-2 font-mono-num text-xs">{d.event_date || "—"}</td>
                  <td className="px-3 py-2 font-mono-num text-xs text-[var(--text-secondary)]">{d.invoice_number || "—"}</td>
                  <td className="px-3 py-2 text-[var(--text-secondary)] truncate max-w-[140px]">{d.contact_person || "—"}</td>
                  <td className="px-3 py-2 font-mono-num text-xs text-[var(--text-secondary)] truncate max-w-[180px]" title={d.email}>{d.email || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-semibold">
                    {formatMoney(d.amount, d.currency)}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] ${meta.bg} ${meta.fg}`}>
                      <StatusIcon size={11} />
                      {lang === "es" ? meta.es : meta.en}
                    </span>
                    {d.overdue && d.status === "pending" && (
                      <span
                        className="ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[var(--status-negative-bg)] text-[var(--status-negative)]"
                        data-testid={`debt-overdue-${d.id}`}
                        title={lang === "es" ? "Vencido" : "Overdue"}
                      >
                        <AlertTriangle size={9} />
                        {lang === "es" ? "Vencido" : "Overdue"}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="file"
                      accept="application/pdf,image/*"
                      className="hidden"
                      ref={(el) => { attachRef.current[d.id] = el; }}
                      onChange={(e) => uploadAttachment(d.id, e.target.files)}
                      data-testid={`debt-file-input-${d.id}`}
                    />
                    {d.file_id ? (
                      <div className="flex items-center gap-1">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-[var(--primary)]/15 text-[var(--primary)] font-medium max-w-[130px] truncate"
                          title={d.file_name || ""}
                          data-testid={`debt-attached-${d.id}`}
                        >
                          <Paperclip size={10} />
                          {d.file_name || (lang === "es" ? "PDF" : "PDF")}
                        </span>
                        <button
                          onClick={() => detachAttachment(d)}
                          className="text-[var(--text-muted)] hover:text-[var(--status-negative)]"
                          title={lang === "es" ? "Quitar adjunto" : "Remove attachment"}
                          data-testid={`debt-detach-${d.id}`}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => openAttachPicker(d.id)}
                        className="text-[10px] text-[var(--text-muted)] hover:text-[var(--primary)] underline decoration-dotted"
                        data-testid={`debt-attach-btn-${d.id}`}
                      >
                        <Paperclip size={11} className="inline mr-0.5" />
                        {lang === "es" ? "Adjuntar factura" : "Attach invoice"}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[var(--text-secondary)] text-xs truncate max-w-[220px]" title={d.notes || ""}>
                    {d.notes || "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-0.5">
                      {d.status === "pending" && (
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => sendOne(d)} title={lang === "es" ? "Enviar recordatorio" : "Send reminder"} data-testid={`debt-send-${d.id}`}>
                          <Mail size={13} />
                        </Button>
                      )}
                      {d.status === "pending" && (
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[var(--status-positive)]" onClick={() => markPaid(d)} title={lang === "es" ? "Marcar pagada" : "Mark paid"} data-testid={`debt-mark-paid-${d.id}`}>
                          <CheckCircle2 size={13} />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(d)} title={lang === "es" ? "Editar" : "Edit"} data-testid={`debt-edit-${d.id}`}>
                        <Pencil size={13} />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[var(--status-negative)]" onClick={() => handleDelete(d)} title={lang === "es" ? "Eliminar" : "Delete"} data-testid={`debt-delete-${d.id}`}>
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {!loading && visibleDebts.length > 0 && (
            <tfoot data-testid="debts-footer">
              <tr className="border-t-2 border-[var(--border-color)] bg-[var(--surface-hover)]/40 font-semibold">
                <td colSpan={8} className="px-3 py-3 uppercase text-xs tracking-wider">
                  {lang === "es" ? `Total visible · ${visibleDebts.length} deudas` : `Visible total · ${visibleDebts.length} debts`}
                </td>
                <td className="px-3 py-3 text-right font-mono-num" data-testid="debts-visible-total">
                  {formatMoney(total, baseCurrency)}
                </td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <DebtDialog
        open={dialogOpen}
        onClose={closeDialog}
        debt={editing}
        setDebt={setEditing}
        onSubmit={submitDialog}
        lang={lang}
      />
    </div>
  );
}

// ============================================================ Sort header
function SortHeader({ label, column, sort, onToggle, align = "left", testid }) {
  const active = sort.column === column;
  const Icon = active && sort.order === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      className={`text-${align} px-3 py-2 font-medium cursor-pointer select-none hover:text-[var(--text-primary)]`}
      onClick={() => onToggle(column)}
      data-testid={testid}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>
        {label}
        <Icon size={10} className={active ? "text-[var(--primary)]" : "opacity-30"} />
      </span>
    </th>
  );
}

// ============================================================ Summary Card
function SummaryCard({ label, count, amount, currency, color, testid }) {
  const colorMap = {
    amber: "border-amber-200 dark:border-amber-800/50 text-amber-700 dark:text-amber-300",
    red:   "border-red-200 dark:border-red-800/50 text-red-700 dark:text-red-300",
    green: "border-emerald-200 dark:border-emerald-800/50 text-emerald-700 dark:text-emerald-300",
    gray:  "border-[var(--border-color)] text-[var(--text-muted)]",
  };
  return (
    <div className={`bg-[var(--surface)] border rounded-lg px-4 py-3 ${colorMap[color] || colorMap.gray}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider font-medium mb-1">{label}</p>
      <p className="text-2xl font-display font-bold font-mono-num leading-none" data-testid={`${testid}-amount`}>
        {formatMoney(amount, currency)}
      </p>
      <p className="text-[11px] mt-1 opacity-70" data-testid={`${testid}-count`}>{count} {count === 1 ? "deuda" : "deudas"}</p>
    </div>
  );
}

// ============================================================ Debt Dialog
function DebtDialog({ open, onClose, debt, setDebt, onSubmit, lang }) {
  const attachInput = useRef(null);
  if (!debt) return null;
  const set = (k, v) => setDebt((prev) => ({ ...prev, [k]: v }));
  const attachedFile = debt.__attachedFile || null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="debt-dialog">
        <DialogHeader>
          <DialogTitle>
            {debt.id
              ? (lang === "es" ? "Editar deuda" : "Edit debt")
              : (lang === "es" ? "Nueva deuda" : "New debt")}
          </DialogTitle>
          <DialogDescription>
            {lang === "es"
              ? "Rellena los datos del cobro pendiente. El estado 'Vencido' se calcula automáticamente en base a la fecha del evento y los días de vencimiento."
              : "Fill in the outstanding payment details. 'Overdue' is auto-computed based on the event date and due days."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Cliente" : "Client"}</Label>
            <ClientAutocomplete
              value={debt.client_name}
              onChange={(v) => set("client_name", v)}
              onPick={(s) => {
                // Batch-update: name + optional metadata in one setState so we
                // don't lose the CIF if the user types then quickly picks.
                setDebt((prev) => ({
                  ...prev,
                  client_name: s.name,
                  client_tax_id: s.tax_id || prev.client_tax_id || "",
                  email: s.email || prev.email || "",
                  contact_person: s.contact_person || prev.contact_person || "",
                }));
              }}
              lang={lang}
            />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-wider">CIF / NIF</Label>
            <Input
              value={debt.client_tax_id || ""}
              onChange={(e) => set("client_tax_id", e.target.value.toUpperCase())}
              placeholder="B12345678"
              className="font-mono-num uppercase"
              data-testid="debt-tax-id"
            />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Concepto" : "Concept"}</Label>
            <Input value={debt.concept || ""} onChange={(e) => set("concept", e.target.value)} data-testid="debt-concept" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Fecha evento" : "Event date"}</Label>
            <Input type="date" value={debt.event_date || ""} onChange={(e) => set("event_date", e.target.value)} data-testid="debt-event-date" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Nº factura" : "Invoice #"}</Label>
            <Input value={debt.invoice_number || ""} onChange={(e) => set("invoice_number", e.target.value)} data-testid="debt-invoice-number" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Persona de contacto" : "Contact person"}</Label>
            <Input value={debt.contact_person || ""} onChange={(e) => set("contact_person", e.target.value)} data-testid="debt-contact-person" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider">Email</Label>
            <Input type="email" value={debt.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="debt-email" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Importe" : "Amount"}</Label>
            <Input type="number" step="0.01" value={debt.amount ?? ""} onChange={(e) => set("amount", e.target.value)} className="font-mono-num" data-testid="debt-amount" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Estado" : "Status"}</Label>
            <select
              value={debt.status || "pending"}
              onChange={(e) => set("status", e.target.value)}
              className="h-9 w-full rounded-md border border-[var(--border-color)] bg-[var(--surface)] px-2 text-sm"
              data-testid="debt-status"
            >
              <option value="pending">{lang === "es" ? "Pendiente" : "Pending"}</option>
              <option value="paid">{lang === "es" ? "Pagado" : "Paid"}</option>
              <option value="cancelled">{lang === "es" ? "Anulado" : "Cancelled"}</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider" title={lang === "es" ? "Días tras la fecha evento antes de marcar como vencido" : "Days after event date before flagging as overdue"}>
              {lang === "es" ? "Días vencimiento" : "Due days"}
            </Label>
            <Input type="number" min="0" value={debt.due_days ?? 30} onChange={(e) => set("due_days", e.target.value)} className="font-mono-num" data-testid="debt-due-days" />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Observaciones" : "Notes"}</Label>
            <Input value={debt.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="debt-notes" />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Factura emitida (PDF/imagen)" : "Issued invoice (PDF/image)"}</Label>
            <div className="flex items-center gap-2 flex-wrap">
              {attachedFile ? (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-[var(--border-color)] bg-[var(--surface)] text-xs" data-testid="debt-dialog-attached">
                  <Paperclip size={12} className="text-[var(--primary)]" />
                  <span className="truncate max-w-[240px]">{attachedFile.name}</span>
                  <button
                    type="button"
                    onClick={() => { set("__attachedFile", null); if (attachInput.current) attachInput.current.value = ""; }}
                    className="text-[var(--text-muted)] hover:text-[var(--status-negative)]"
                    title={lang === "es" ? "Quitar" : "Remove"}
                    data-testid="debt-dialog-attach-remove"
                  >
                    <X size={12} />
                  </button>
                </span>
              ) : debt.has_attachment ? (
                <span className="text-xs text-[var(--text-muted)]">
                  {lang === "es" ? "Ya tiene una factura adjunta (puedes gestionarla desde la fila)" : "Already has an attachment (manage from the row)"}
                </span>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => attachInput.current?.click()}
                  className="h-8 text-xs"
                  data-testid="debt-dialog-attach-btn"
                >
                  <Paperclip size={12} className="mr-1" />
                  {lang === "es" ? "Adjuntar factura" : "Attach invoice"}
                </Button>
              )}
              <input
                ref={attachInput}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) set("__attachedFile", f);
                }}
              />
            </div>
            <p className="text-[10px] text-[var(--text-muted)]">
              {lang === "es"
                ? "Se adjuntará automáticamente al enviar recordatorios por email."
                : "Will be automatically attached to email reminders."}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{lang === "es" ? "Cancelar" : "Cancel"}</Button>
          <Button onClick={onSubmit} className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white" data-testid="debt-submit">
            {lang === "es" ? "Guardar" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
